import { RailcarResponseDto } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { RailcarsFormDialog } from '@planning/pages/Railcars/components/RailcarsFormDialog'
import { RailcarsViewStore } from '@planning/pages/Railcars/stores/RailcarsViewStore'
import { formatRailcarName, unformatRailcarName } from '@planning/utils/railcar-utils'
import { useTranslate } from '@tolgee/react'
import { TomAutoComplete } from '@tom-ui/ui'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'

interface IProps {
  hasError: boolean
  required?: boolean
  label: string
  value?: number
  onChange: (railcarId?: number) => void
  excludeIds?: number[]
  disabled?: boolean
}

export const RailcarAutocomplete = observer(
  ({ hasError, required, label, value, onChange, excludeIds = [], disabled }: IProps) => {
    const { t } = useTranslate()
    const { railcarItemStore, appViewStore } = usePlanningStore()

    const [inputValue, setInputValue] = useState('')

    const railcarsViewStore = useMemo(
      () => new RailcarsViewStore(railcarItemStore, appViewStore),
      [railcarItemStore, appViewStore],
    )

    useEffect(() => {
      railcarItemStore.fetch()
    }, [railcarItemStore])

    useEffect(() => {
      if (!railcarsViewStore.isDialogOpen && inputValue) {
        const newlyCreated = railcarItemStore
          .getRailcars()
          .find(rc => unformatRailcarName(rc.name) === unformatRailcarName(inputValue))

        if (newlyCreated) {
          onChange(newlyCreated.id)
        }
      }
    }, [railcarsViewStore.isDialogOpen, inputValue, railcarItemStore, onChange])

    const baseOptions = computed(() => {
      const all = railcarItemStore.getRailcars()
      return all
        .filter(rc => !excludeIds.includes(rc.id) || rc.id === value)
        .map(rc => ({ ...rc, name: formatRailcarName(rc.name) }))
    }).get()

    const handleAddNew = (inputValue?: string) => {
      if (inputValue) {
        railcarsViewStore.setDefaultRailcarNumber(inputValue)
      }
      railcarsViewStore.toggleDialog(true)
    }

    const filterOptions = (
      options: RailcarResponseDto[],
      { inputValue }: { inputValue: string },
    ) => {
      if (!inputValue) return options

      const filtered = options.filter(option => {
        const unformattedCondition = unformatRailcarName(option.name).includes(
          inputValue.toLowerCase(),
        )
        const rawCondition = option.name.toLowerCase().includes(inputValue.toLowerCase())
        return unformattedCondition || rawCondition
      })

      const exists = filtered.some(
        opt =>
          unformatRailcarName(opt.name) === unformatRailcarName(inputValue) ||
          opt.name.toLowerCase() === inputValue.toLowerCase(),
      )

      if (!exists && inputValue.trim() !== '') {
        filtered.push({
          id: 0,
          name: inputValue,
          railcarType: '',
          maxPayload: 0,
          length: 0,
        } as RailcarResponseDto)
      }

      return filtered
    }

    const resolvedValue = useMemo(() => {
      return baseOptions.find(x => x.id === value)
    }, [baseOptions, value])

    return (
      <>
        <TomAutoComplete<RailcarResponseDto>
          label={label}
          options={baseOptions}
          defaultValue={resolvedValue}
          error={hasError}
          required={required}
          helperText={hasError ? t('fieldIsRequired', 'Field is required') : undefined}
          getOptionLabel={option => option.name}
          entityKey='id'
          filterOptions={filterOptions}
          onChangeFilter={val => setInputValue(val ?? '')}
          onChangeEntity={entity => {
            if (entity?.id === 0) {
              handleAddNew(inputValue)
              return
            }
            onChange(entity?.id)
          }}
          onAddNew={(_, val) => handleAddNew(val)}
          disabled={disabled}
          dataCy='railcar-autocomplete'
        />

        <RailcarsFormDialog store={railcarsViewStore} />
      </>
    )
  },
)
